/**
 * Page 7 usage/cost types and formatting (Phase 8A).
 *
 * Every money value is **integer micro-USD** on the wire and stays an integer until the last moment
 * in `formatMicros`. Floating-point money is how a cost report ends up a cent off the invoice with no
 * way to find out where.
 */

export type UsageWindow = "today" | "7d" | "30d";

export const USAGE_WINDOWS: Array<{ value: UsageWindow; label: string }> = [
  { value: "today", label: "24 h" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
];

export interface BreakdownRow {
  key: string;
  events: number;
  quantity: number;
  cost_micros: number;
  shadow_cost_micros: number;
}

export interface Money {
  cost_micros: number;
  shadow_cost_micros: number;
}

export interface ByokUsage {
  events: number;
  tokens: number;
  cost_micros: number;
  billable_to_platform: boolean;
}

export interface HeadroomRow {
  key: string;
  class: string;
  period: string;
  consumed: number;
  limit: number;
  remaining: number;
  /** `null` when the ledger cannot answer: a missing measurement is not headroom. */
  percent: number | null;
  reset_at: string | null;
  measured: boolean;
  enforced: boolean;
}

export interface TenantUsage {
  window: string;
  since: string;
  as_of: string;
  account_id: string;
  totals: Money;
  events: number;
  by_provider: BreakdownRow[];
  by_resource: BreakdownRow[];
  by_purpose: BreakdownRow[];
  byok: ByokUsage;
  quota: HeadroomRow[];
  note: string;
}

export interface ResourceRow {
  resource: string;
  quantity: number;
  events: number;
  cost_micros: number;
  shadow_cost_micros: number;
  limit: number | null;
  period: string | null;
  percent: number | null;
  burn_per_hour: number | null;
  hours_to_limit: number | null;
}

export interface ProviderRow {
  provider: string;
  cost_micros: number;
  shadow_cost_micros: number;
  events: number;
  resources: ResourceRow[];
}

export interface BuildVsRun {
  build_tokens: number;
  build_cost_micros: number;
  build_events: number;
  run_tokens: number;
  run_cost_micros: number;
  run_events: number;
  /** `null` until both sides have data — a ratio against zero would read as "runs are free". */
  ratio: number | null;
}

export interface TenantRow {
  account_id: string;
  label: string;
  events: number;
  cost_micros: number;
  shadow_cost_micros: number;
}

export interface ReconciliationRow {
  provider: string;
  resource: string;
  local_quantity: number;
  provider_quantity: number | null;
  drift_permille: number | null;
  status: "ok" | "under" | "over" | "unknown" | string;
  source_kind: string;
}

export interface ReconciliationSummary {
  day: string | null;
  rows: ReconciliationRow[];
  needs_attention: number;
  last_polled_at: string | null;
}

export interface TripwireRow {
  key: string;
  threshold: number;
  observed: number;
  tripped: boolean;
  enforces: boolean;
}

export interface AdminUsage {
  window: string;
  since: string;
  as_of: string;
  totals: Money;
  events: number;
  providers: ProviderRow[];
  by_purpose: BreakdownRow[];
  build_vs_run: BuildVsRun;
  tenants: TenantRow[];
  reconciliation: ReconciliationSummary;
  tripwires: TripwireRow[];
  note: string;
}

/**
 * Formats integer micro-USD as dollars, with the precision the amount deserves.
 *
 * A single email send is $0.0018 and a month of traffic is $4.27, so one fixed precision is wrong in
 * one direction or the other: two decimals hides every small charge, six makes a large one unreadable.
 * The rule is by magnitude — cents above a dollar, four decimals above a cent, six below — and it is
 * all integer arithmetic, so no float ever rounds a total.
 */
export function formatMicros(micros: number): string {
  const sign = micros < 0 ? "-" : "";
  const absolute = Math.abs(Math.trunc(micros));
  const dollars = Math.floor(absolute / 1_000_000);
  const remainder = absolute % 1_000_000;

  if (dollars >= 1) {
    // Round to the nearest cent, then carry into the dollars when it rounds up to 100.
    let cents = Math.round(remainder / 10_000);
    let whole = dollars;
    if (cents === 100) {
      whole += 1;
      cents = 0;
    }
    return `${sign}$${whole.toLocaleString("en-US")}.${String(cents).padStart(2, "0")}`;
  }
  if (remainder >= 10_000) {
    return `${sign}$0.${String(Math.round(remainder / 100)).padStart(4, "0")}`;
  }
  // Sub-cent: six decimals with trailing zeros trimmed, but never fewer than two.
  const fraction = String(remainder).padStart(6, "0").replace(/0+$/, "").padEnd(2, "0");
  return `${sign}$0.${fraction}`;
}

/**
 * A bar's fill, as a percentage clamped to 100.
 *
 * `null` in, `null` out: an unmeasured limit must render as "not measured", never as an empty bar,
 * because an empty bar reads as "plenty of headroom" for a quota nobody is counting.
 */
export function barPercent(percent: number | null): number | null {
  if (percent === null || !Number.isFinite(percent)) return null;
  return Math.max(0, Math.min(100, percent));
}

/** The colour band for a bar: calm until 60%, warning to 85%, over past that. */
export function barTone(percent: number | null): "ok" | "warn" | "over" | "unknown" {
  if (percent === null) return "unknown";
  if (percent >= 100) return "over";
  if (percent >= 85) return "warn";
  return "ok";
}

/** "in 3 h 20 m" for a projection, or a dash when there is not enough data to project. */
export function formatHours(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours) || hours <= 0) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} m`;
  if (hours < 48) {
    const whole = Math.floor(hours);
    const minutes = Math.round((hours - whole) * 60);
    return minutes > 0 ? `${whole} h ${minutes} m` : `${whole} h`;
  }
  return `${Math.round(hours / 24)} d`;
}

/** Turns the admin payload into the CSV the page offers for download. */
export function adminUsageCsv(payload: AdminUsage): string {
  const lines = ["provider,resource,quantity,events,cost_micros,shadow_cost_micros,limit,percent"];
  for (const provider of payload.providers) {
    for (const resource of provider.resources) {
      lines.push(
        [
          provider.provider,
          resource.resource,
          resource.quantity,
          resource.events,
          resource.cost_micros,
          resource.shadow_cost_micros,
          resource.limit ?? "",
          resource.percent === null ? "" : resource.percent.toFixed(2),
        ].join(",")
      );
    }
  }
  return lines.join("\n");
}
